import React, { useState, useEffect, useRef, useCallback } from "react";

import FilterData from "../../api/FilterData";
import Memory from "../../api/Memory";
import Media from "../../api/Media";
import {
  DIRECTIONS,
  resolveDirectionFromStorage,
} from "../../utils/softVersionCompareUtils";

import { observer } from "mobx-react-lite";

import ApiData from "../../api/ApiData";
import {
  getSoftwareTypeFromSession,
  setSoftwareTypeInSession,
} from "../../utils/softVersionClientTypeUtils";
import { ReportsVersionField } from "../reports/ReportsVersionField";

export const FilterByVersion = observer(
  ({ versionFilterOn, setVersionFilterOn, v4Layout, versionUiResetKey = 0 }) => {
    const [valueVersion, setValueVersion] = useState(
      sessionStorage.getItem("soft_version") ?? "",
    );

    const [softwareTypeMode, setSoftwareTypeMode] = useState(() =>
      getSoftwareTypeFromSession(),
    );

    const [filterName, setFilterName] = useState(() => {
      const item = resolveDirectionFromStorage();
      Memory.setVersionReduction(item.memory);
      return item.key;
    });

    const [clsOnBtn, setClsOnBtn] = useState(
      versionFilterOn ? "btn_active_period" : "btn_deactive_period",
    );

    const [valueBtn, setValueBtn] = useState(versionFilterOn ? "Вкл" : "Откл");

    const prevVersionFilterOnRef = useRef(versionFilterOn);

    const applyDirection = useCallback(
      (label) => {
        const item = DIRECTIONS.find((d) => d.key === label) ?? DIRECTIONS[2];
        setFilterName(item.key);
        Memory.setVersionReduction(item.memory);
        sessionStorage.setItem("condition_version_client", item.key);
        const trimmed = String(valueVersion).trim();
        if (trimmed) {
          FilterData.filterSoftVersionCompany(trimmed);
        }
      },
      [valueVersion],
    );

    const directionVersion = (e) => {
      try {
        const label = e.target?.dataset?.direction || e.target?.textContent;
        if (label === "<") applyDirection("Меньше");
        else if (label === "=") applyDirection("Равно");
        else if (label === ">") applyDirection("Больше");
        else if (DIRECTIONS.some((d) => d.key === label)) applyDirection(label);
      } catch (err) {
        console.error(err);
      }
    };

    const hendlerValueVersion = (e) => {
      const next = e.target.value;
      setValueVersion(next);

      if (v4Layout) {
        const trimmed = next.trim();
        if (trimmed) {
          sessionStorage.setItem("soft_version", next);
          if (!versionFilterOn) {
            setVersionFilterOn(true);
          }
          FilterData.filterSoftVersionCompany(trimmed);
        } else {
          sessionStorage.removeItem("soft_version");
          sessionStorage.removeItem("condition_version_client");
          setVersionFilterOn(false);
          FilterData.showAllClients();
        }
        return;
      }

      sessionStorage.removeItem("soft_version");
      if (!versionFilterOn && next.trim()) {
        setVersionFilterOn(true);
        setClsOnBtn("btn_active_period");
        setValueBtn("Вкл");
      }
      FilterData.filterSoftVersionCompany(next);
    };

    useEffect(() => {
      if (ApiData.company.clients_soft_data === null) {
        return;
      }
      const trimmed = String(valueVersion).trim();
      if (v4Layout) {
        if (!trimmed || !versionFilterOn) {
          return;
        }
        FilterData.filterSoftVersionCompany(trimmed);
        return;
      }
      if (versionFilterOn && trimmed) {
        FilterData.filterSoftVersionCompany(trimmed);
      }
    }, [
      ApiData.company.clients_soft_data,
      valueVersion,
      filterName,
      v4Layout,
      versionFilterOn,
      softwareTypeMode,
    ]);

    const onSoftwareTypeModeChange = useCallback(
      (mode) => {
        setSoftwareTypeInSession(mode);
        setSoftwareTypeMode(mode);
        const trimmed = String(valueVersion).trim();
        if (v4Layout) {
          if (trimmed && versionFilterOn) {
            FilterData.filterSoftVersionCompany(trimmed);
          }
        } else if (trimmed && versionFilterOn) {
          FilterData.filterSoftVersionCompany(trimmed);
        }
      },
      [valueVersion, v4Layout, versionFilterOn],
    );

    const onFilter = () => {
      if (!versionFilterOn) {
        setVersionFilterOn(true);
        setClsOnBtn("btn_active_period");
        setValueBtn("Вкл");
        ApiData.overkillSoftClients();
        FilterData.filterSoftVersionCompany(valueVersion);
      } else {
        setVersionFilterOn(false);
        setClsOnBtn("btn_deactive_period");
        setValueBtn("Откл");
        FilterData.showAllClients();
        sessionStorage.removeItem("soft_version");
        sessionStorage.removeItem("condition_version_client");
      }
    };

    useEffect(() => {
      if (prevVersionFilterOnRef.current && !versionFilterOn) {
        setClsOnBtn("btn_deactive_period");
        setValueBtn("Откл");
        setValueVersion("");
        setFilterName("Больше");
        Memory.setVersionReduction(2);
        FilterData.showAllClients();
        sessionStorage.removeItem("soft_version");
        sessionStorage.removeItem("condition_version_client");
      }
      prevVersionFilterOnRef.current = versionFilterOn;
    }, [versionFilterOn]);

    useEffect(() => {
      if (!v4Layout || !versionUiResetKey) {
        return;
      }
      setValueVersion("");
      setFilterName("Больше");
      Memory.setVersionReduction(2);
      FilterData.showAllClients();
    }, [versionUiResetKey, v4Layout]);

    const enableVersionFilter = () => {
      if (!versionFilterOn && valueVersion.trim()) {
        setVersionFilterOn(true);
        setClsOnBtn("btn_active_period");
        setValueBtn("Вкл");
        if (!v4Layout) {
          ApiData.overkillSoftClients();
        }
        FilterData.filterSoftVersionCompany(valueVersion);
      }
    };

    if (v4Layout) {
      return (
        <div className="reports-version-controls">
          <div className="subscription-period-chips">
            {DIRECTIONS.map((d) => (
              <button
                key={d.key}
                type="button"
                data-direction={d.key}
                className={`subscription-period-chip${
                  filterName === d.key
                    ? " subscription-period-chip--active"
                    : ""
                }`}
                onClick={directionVersion}
              >
                {!Media.mobile ? d.key : d.short}
              </button>
            ))}
          </div>
          <ReportsVersionField
            value={valueVersion}
            onChange={hendlerValueVersion}
            onFocus={enableVersionFilter}
            softwareTypeMode={softwareTypeMode}
            onSoftwareTypeModeChange={onSoftwareTypeModeChange}
          />
        </div>
      );
    }

    return (
      <div className="soft_filter_version_container">
        <span className="text_soft_filter_version">Поиск по версии</span>
        <span
          className={clsOnBtn}
          style={{ width: "65px", marginLeft: "5px" }}
          onClick={onFilter}
        >
          {valueBtn}.
        </span>
        {versionFilterOn ? (
          <input
            value={valueVersion}
            onChange={hendlerValueVersion}
            className="inp_soft_filter_version"
            type="text"
          />
        ) : (
          <></>
        )}
      </div>
    );
  },
);
